/**
 * A button that opens a dialog holding one `ActionForm` — "New task", "New client",
 * "Record an expense".
 *
 * Four of the five studio screens have one of these in the topbar or beside a section
 * head, and every one of them is the same three pieces of state: is it open, did the
 * call succeed, and close it when it did. Written out per page, the third is the one
 * that gets forgotten — a dialog that stays open over a form that has already run is
 * how somebody creates two tasks.
 *
 * The money confirmation is **inside** the form, not here (`ActionForm`), which is
 * why this is a wrapper rather than a dialog with a confirm button: a `$` action opens
 * a second dialog over this one, and Radix handles that correctly as long as nothing
 * else is trying to close the first.
 */

import { useCallback, useEffect, useId, useRef, useState, type ReactNode } from 'react'

import type { StudioActionName } from '../../api/studio.js'
import { Button, type ButtonVariant } from '../portal/Button.js'
import { Dialog } from '../portal/Overlay.js'
import type { FormValues } from '../portal/schemaForm.js'
import { ActionForm, type ActionConfirm, type ActionSchemaOverride } from './ActionForm.js'

/** Longer than this and a note is given the wide panel rather than the small one. */
const FIT_WIDE_CHARS = 240

export type ActionDialogProps = {
  readonly action: StudioActionName
  /**
   * The page's own schema for this action's one map field — `ActionForm`'s, passed
   * straight through. One caller has one: the Widgets section, over a widget's
   * snapshotted `config_schema`.
   */
  readonly schema?: ActionSchemaOverride | undefined
  /** The button's own words — "New task". Also the dialog's title unless `title` is set. */
  readonly label: string
  readonly title?: string | undefined
  /** Which task, invoice or client this window is about — `Dialog`'s own note. */
  readonly about?: ReactNode | undefined
  /** The dismiss control's words, when "Close" is the wrong one — `Dialog` says why. */
  readonly dismissLabel?: string | undefined
  /** The submit button's words. `label` again unless the form's verb is different. */
  readonly submitLabel?: string | undefined
  /** The submit button's colour. `primary` unless the move cannot be undone. */
  readonly submitVariant?: ButtonVariant | undefined
  readonly variant?: ButtonVariant | undefined
  readonly size?: 'sm' | undefined
  /**
   * Opens the dialog instead of the default labelled `Button`. The list's Who /
   * Due / Note cells pass the cell content so the trigger looks like the cell.
   * `label` stays required — it is still the title fallback and the default
   * submit words.
   */
  readonly trigger?: ReactNode | undefined
  /** Fields set by the page and not shown — the client this dialog is about. */
  readonly fixed?: Readonly<Record<string, unknown>> | undefined
  /** A refused `fixed.if_was` may be overridden by pressing again — `ActionForm`'s, passed through. */
  readonly insist?: boolean | undefined
  /** The stamp of the row `fixed.if_was` was read from — `ActionForm`'s, passed through. */
  readonly stamp?: string | undefined
  /**
   * True while the button that opens this window should wait — for a move of the same
   * surface's own that is still on its way, as the buttons beside it do. A window
   * opened then is opened over a row that has not been read again, and its condition
   * (`ActionForm` `useOpenedFrom`) is the state the reader has just changed. For the
   * window's own button only: a `trigger` handed in is its owner's to disable.
   *
   * **The wait does not cost the reader their place.** A browser takes the focus off
   * a control that is disabled under it, and a shortcut is how that happens here: `s`
   * on the task page starts the task with the cursor on "Give it back", which then
   * waits. When the wait is over the button is given the focus again — if nothing
   * else has taken it meanwhile.
   */
  readonly disabled?: boolean | undefined
  /** Choices a picker must not offer here — `ActionForm`'s, passed through. */
  readonly without?: Readonly<Record<string, readonly string[]>> | undefined
  /** Draw only these fields — cell editors pass one name (e.g. `instructions`). */
  readonly only?: readonly string[] | undefined
  /** Optional fields this dialog refuses blank — `ActionForm`'s, passed through. */
  readonly required?: readonly string[] | undefined
  readonly defaults?: Readonly<Record<string, unknown>> | undefined
  /** `defaults` are a starting point, not a stored row — `ActionForm`'s, passed through. */
  readonly suggested?: boolean | undefined
  /**
   * True when the window opens already holding something the reader wrote.
   *
   * `unsaved` below is the form's own "has anything been typed here", and a value
   * carried in as a default has not been typed *here* — so the full New Task window,
   * opened from the quick one with the title somebody had just written, was a clean
   * form and a click beside it threw that title away. The page knows, and says so.
   */
  readonly unsavedAtOpen?: boolean | undefined
  /** Wider panel for a two-column form (#39). */
  readonly wide?: boolean | undefined
  /**
   * The text this dialog is about to edit, so the window can size itself to it.
   *
   * The owner, 17 Sep: a note opens "una finestra la cui grandezza si adatta alla
   * lunghezza del testo (non deve essere ingrandita a mano)". A long note gets the
   * wide panel, and `fit` lets the box grow with what is in it rather than being
   * dragged. Length, not a guess at line breaks: a note is one paragraph as often as
   * it is ten lines.
   */
  readonly fitContent?: string | undefined
  /**
   * Passed through to `ActionForm`: a second control beside the commit button.
   * A function is handed the form's current values — see `ActionForm`'s own note.
   */
  readonly beside?: ReactNode | ((values: FormValues, working: boolean) => ReactNode) | undefined
  /** Passed through: put the cursor in the first field when the window opens. */
  readonly autoFocusFirstField?: boolean | undefined
  /** Two-column field layout — `ActionForm`'s, passed through. */
  readonly columns?: 2 | undefined
  /** Fields that span both columns when `columns` is 2 (#40). */
  readonly fullWidth?: readonly string[] | undefined
  /**
   * Controlled open — used when the trigger lives in a popover that must close
   * before the dialog mounts (list overflow menu), so the dialog is not unmounted
   * with the menu.
   */
  readonly open?: boolean | undefined
  readonly onOpenChange?: ((open: boolean) => void) | undefined
  /**
   * Run after the action's own answer lands. The dialog is closed by then — by that
   * answer: it is not shut while its write is on its way (`inFlight`, below).
   */
  readonly onDone?: ((output: unknown) => void) | undefined
  /** A line above the fields. */
  readonly children?: ReactNode | undefined
  /** `ActionForm`'s question before sending, passed through. */
  readonly confirm?: ((values: FormValues) => ActionConfirm | null) | undefined
}

/**
 * The `id` for a button that waits (`disabled`), which is **given the cursor back when
 * the wait is over** if it had it when the wait began.
 *
 * A browser takes the focus off a control that is disabled under it, and gives it to
 * nobody: `s` starts a task with the cursor on the button beside Start, that button
 * waits for Start to land, and the reader on a keyboard was back at the top of the
 * page. Whether it had the focus is asked while it is being drawn, which is before
 * the button in the page has been told to wait — afterwards the browser has already
 * moved the focus on.
 */
export function useFocusBackAfterWaiting(disabled: boolean | undefined): string {
  const id = useId()
  const waitedWithFocus = useRef(false)
  if (disabled === true && document.activeElement?.id === id) {
    waitedWithFocus.current = true
  }
  useEffect(() => {
    if (disabled === true || !waitedWithFocus.current) return
    waitedWithFocus.current = false
    // Only where the focus is nobody's: a reader who has moved on is not pulled back.
    const now = document.activeElement
    if (now !== null && now !== document.body) return
    // Where the button is, and not the page scrolled to it: the reader's place, not a new one.
    document.getElementById(id)?.focus({ preventScroll: true })
  }, [disabled, id])
  return id
}

export function ActionDialog({
  action,
  schema,
  label,
  title,
  about,
  dismissLabel,
  submitLabel,
  submitVariant,
  variant,
  size,
  trigger,
  fixed,
  disabled,
  insist,
  stamp,
  without,
  only,
  required,
  defaults,
  suggested,
  unsavedAtOpen,
  wide,
  fitContent,
  beside,
  autoFocusFirstField,
  columns,
  fullWidth,
  open: openProp,
  onOpenChange: onOpenChangeProp,
  onDone,
  children,
  confirm,
}: ActionDialogProps) {
  const [uncontrolled, setUncontrolled] = useState(false)
  const controlled = openProp !== undefined
  const open = controlled ? openProp : uncontrolled
  /*
   * **A window stays until its own write has been answered.**
   *
   * Shutting unmounts the form, and the form is where a refusal is said: Escape
   * pressed while the button read "Working…" shut the window, the write was refused a
   * moment later, and nothing anywhere said the change had not been made. An accepted
   * write shuts the window itself, so staying costs nothing a reader was going to
   * keep. And whatever follows the answer (`onDone`) runs with the window it was
   * pressed in still the one that is open: the bulk bar's batch is made from that
   * window's rows (`BulkBar` `said`), and a second window opened over the first's
   * unanswered write handed it the second one's.
   *
   * `ActionForm` says when (`onSending`). The state is what is drawn (`Dialog`
   * `held`); the ref is what the very next key press is answered from, drawn or not.
   *
   * **For as long as the write is, which is not for ever**: a write is answered in
   * time or said to have had no answer (`api/hooks.ts` `WRITE_ANSWER_MS`), and is
   * never held back for the browser to come on line. Letting the *window* go after a
   * while instead was tried: the write was then still out, and its answer came to
   * whatever window was open by then and shut it.
   */
  const [sending, setSending] = useState(false)
  const inFlight = useRef(false)
  const onSending = useCallback((now: boolean): void => {
    inFlight.current = now
    setSending(now)
  }, [])
  const setOpen = (next: boolean): void => {
    if (!next && inFlight.current) return
    if (!controlled) setUncontrolled(next)
    onOpenChangeProp?.(next)
  }
  /*
   * Whether the form holds anything the reader has not saved — the form's own answer
   * (`ActionForm` `onDirtyChange`), kept here because the window is what a stray
   * click closes.
   *
   * Closing unmounts the form, so a click on the page beside a half-written note
   * threw the note away without a word. While this is true the window refuses an
   * outside click, exactly as a money confirmation always has (`Dialog` `modalOnly`),
   * and nothing else changes: Escape and the dismiss button are things somebody did
   * on purpose and still close it — its own write on its way aside (`sending`, above)
   * — and a form nobody has typed in still closes on a click beside it.
   */
  const [unsaved, setUnsaved] = useState(false)

  /* The window's own button, given the cursor back where it waited with it. */
  const triggerId = useFocusBackAfterWaiting(disabled)

  /* Past a paragraph a note wants the two-column panel's room; under it, the small one. */
  const roomy = fitContent !== undefined && fitContent.length > FIT_WIDE_CHARS

  return (
    <Dialog
      open={open}
      onOpenChange={setOpen}
      title={title ?? label}
      about={about}
      dismissLabel={dismissLabel}
      modalOnly={unsaved || unsavedAtOpen === true}
      held={sending}
      wide={wide === true || roomy}
      fit={fitContent !== undefined}
      trigger={
        trigger ??
        (controlled ? undefined : (
          <Button id={triggerId} variant={variant} size={size} disabled={disabled}>
            {label}
          </Button>
        ))
      }
    >
      {/*
        Keyed on `open` so the form is a fresh mount every time the dialog is opened.
        Without it, a form that was half filled in and abandoned comes back with the
        old values and — worse — with the previous run's error still under a field.
      */}
      {open ? (
        <ActionForm
          key={String(open)}
          action={action}
          schema={schema}
          fixed={fixed}
          insist={insist}
          stamp={stamp}
          without={without}
          only={only}
          required={required}
          defaults={defaults}
          suggested={suggested}
          columns={columns}
          fullWidth={fullWidth}
          submitLabel={submitLabel ?? label}
          submitVariant={submitVariant}
          beside={beside}
          autoFocusFirstField={autoFocusFirstField}
          confirm={confirm}
          onDirtyChange={setUnsaved}
          onSending={onSending}
          // Save over a stored row with nothing changed: there is nothing to do but shut.
          onUnchanged={() => {
            setOpen(false)
          }}
          onDone={(output) => {
            setOpen(false)
            onDone?.(output)
          }}
        >
          {children}
        </ActionForm>
      ) : null}
    </Dialog>
  )
}
